import { useState, useCallback, useMemo, useEffect } from "react";
import type { PortalSchema, Page, Block, BlockType, BlockUpdate, Snippet, PortalSettings } from "../../shared/schema";
import {
  createDefaultSchema,
  createDefaultPage,
  createDefaultBlock,
  createDefaultSnippet,
  validatePortalSchema,
} from "../../shared/schema";

export interface UsePortalSchemaReturn {
  // State
  schema: PortalSchema;
  selectedPageId: string | null;
  selectedPage: Page | null;

  // Page selection
  setSelectedPageId: (pageId: string | null) => void;

  // Snippet operations
  addSnippet: (key?: string, value?: string) => void;
  updateSnippet: (snippetId: string, updates: Partial<Omit<Snippet, "id">>) => void;
  removeSnippet: (snippetId: string) => void;

  // Page operations
  addPage: (title?: string) => void;
  updatePage: (pageId: string, updates: Partial<Omit<Page, "id">>) => void;
  removePage: (pageId: string) => void;
  reorderPages: (pageIds: string[]) => void;

  // Block operations
  addBlock: (pageId: string, type: BlockType) => void;
  updateBlock: <T extends Block["type"]>(pageId: string, blockId: string, updates: BlockUpdate<T>) => void;
  removeBlock: (pageId: string, blockId: string) => void;
  reorderBlocks: (pageId: string, blockIds: string[]) => void;

  // Schema operations
  updateSchemaName: (name: string) => void;
  updateSettings: (updates: Partial<PortalSettings>) => void;
  importSchema: (json: unknown) => { success: boolean; errors?: string[] };
  exportSchema: () => PortalSchema;
  resetSchema: () => void;
}

export function usePortalSchema(initialSchema?: PortalSchema | null): UsePortalSchemaReturn {
  const [schema, setSchema] = useState<PortalSchema>(() => initialSchema ?? createDefaultSchema());
  const [selectedPageId, setSelectedPageId] = useState<string | null>(() => {
    const startingSchema = initialSchema ?? createDefaultSchema();
    return startingSchema.pages[0]?.id ?? null;
  });

  // Update schema when initialSchema changes (e.g., after loading from server)
  useEffect(() => {
    if (initialSchema) {
      setSchema(initialSchema);
      setSelectedPageId(initialSchema.pages[0]?.id ?? null);
    }
  }, [initialSchema]);

  // Computed: selected page
  const selectedPage = useMemo(() => {
    return schema.pages.find((p) => p.id === selectedPageId) ?? null;
  }, [schema.pages, selectedPageId]);

  // Helper to update schema with timestamp
  const updateSchema = useCallback((updater: (prev: PortalSchema) => PortalSchema) => {
    setSchema((prev) => ({
      ...updater(prev),
      updatedAt: new Date().toISOString(),
    }));
  }, []);

  // Snippet operations
  const addSnippet = useCallback(
    (key: string = "newSnippet", value: string = "") => {
      const newSnippet = createDefaultSnippet(key, value);
      updateSchema((prev) => ({
        ...prev,
        snippets: [...(prev.snippets ?? []), newSnippet],
      }));
    },
    [updateSchema]
  );

  const updateSnippet = useCallback(
    (snippetId: string, updates: Partial<Omit<Snippet, "id">>) => {
      updateSchema((prev) => ({
        ...prev,
        snippets: (prev.snippets ?? []).map((snippet) =>
          snippet.id === snippetId ? { ...snippet, ...updates } : snippet
        ),
      }));
    },
    [updateSchema]
  );

  const removeSnippet = useCallback(
    (snippetId: string) => {
      updateSchema((prev) => ({
        ...prev,
        snippets: (prev.snippets ?? []).filter((snippet) => snippet.id !== snippetId),
      }));
    },
    [updateSchema]
  );

  // Page operations
  const addPage = useCallback(
    (title: string = "New Page") => {
      const newPage = createDefaultPage(title);
      updateSchema((prev) => ({
        ...prev,
        pages: [...prev.pages, newPage],
      }));
      setSelectedPageId(newPage.id);
    },
    [updateSchema]
  );

  const updatePage = useCallback(
    (pageId: string, updates: Partial<Omit<Page, "id">>) => {
      updateSchema((prev) => ({
        ...prev,
        pages: prev.pages.map((page) => (page.id === pageId ? { ...page, ...updates } : page)),
      }));
    },
    [updateSchema]
  );

  const removePage = useCallback(
    (pageId: string) => {
      updateSchema((prev) => ({
        ...prev,
        pages: prev.pages.filter((page) => page.id !== pageId),
      }));
      // If we removed the selected page, select the first remaining page
      if (selectedPageId === pageId) {
        setSchema((prev) => {
          const remainingPages = prev.pages.filter((p) => p.id !== pageId);
          setSelectedPageId(remainingPages[0]?.id ?? null);
          return prev;
        });
      }
    },
    [updateSchema, selectedPageId]
  );

  const reorderPages = useCallback(
    (pageIds: string[]) => {
      updateSchema((prev) => {
        // Create a map of pages by ID for quick lookup
        const pageMap = new Map(prev.pages.map((p) => [p.id, p]));

        // Reorder pages based on the new ID order
        const reorderedPages = pageIds.reduce<Page[]>((acc, id) => {
          const page = pageMap.get(id);
          if (page !== undefined) {
            acc.push(page);
          }
          return acc;
        }, []);

        return { ...prev, pages: reorderedPages };
      });
    },
    [updateSchema]
  );

  // Block operations
  const addBlock = useCallback(
    (pageId: string, type: BlockType) => {
      const newBlock = createDefaultBlock(type);
      updateSchema((prev) => ({
        ...prev,
        pages: prev.pages.map((page) => (page.id === pageId ? { ...page, blocks: [...page.blocks, newBlock] } : page)),
      }));
    },
    [updateSchema]
  );

  const updateBlock = useCallback(
    <T extends Block["type"]>(pageId: string, blockId: string, updates: BlockUpdate<T>) => {
      updateSchema((prev) => ({
        ...prev,
        pages: prev.pages.map((page) =>
          page.id === pageId
            ? {
                ...page,
                blocks: page.blocks.map((block) => {
                  if (block.id !== blockId) return block;

                  // Runtime verification: block type must match update type
                  if (block.type !== updates.type) {
                    console.error(`Block type mismatch: expected ${updates.type}, got ${block.type}`);
                    return block;
                  }

                  // Safe to spread - types match
                  return { ...block, ...updates };
                }),
              }
            : page
        ),
      }));
    },
    [updateSchema]
  );

  const removeBlock = useCallback(
    (pageId: string, blockId: string) => {
      updateSchema((prev) => ({
        ...prev,
        pages: prev.pages.map((page) =>
          page.id === pageId ? { ...page, blocks: page.blocks.filter((block) => block.id !== blockId) } : page
        ),
      }));
    },
    [updateSchema]
  );

  const reorderBlocks = useCallback(
    (pageId: string, blockIds: string[]) => {
      updateSchema((prev) => ({
        ...prev,
        pages: prev.pages.map((page) => {
          if (page.id !== pageId) return page;

          // Create a map of blocks by ID for quick lookup
          const blockMap = new Map(page.blocks.map((b) => [b.id, b]));

          // Reorder blocks based on the new ID order
          const reorderedBlocks = blockIds
            .map((id) => blockMap.get(id))
            .filter((block): block is Block => block !== undefined);

          return { ...page, blocks: reorderedBlocks };
        }),
      }));
    },
    [updateSchema]
  );

  // Schema operations
  const updateSchemaName = useCallback(
    (name: string) => {
      updateSchema((prev) => ({ ...prev, name }));
    },
    [updateSchema]
  );

  const updateSettings = useCallback(
    (updates: Partial<PortalSettings>) => {
      updateSchema((prev) => ({
        ...prev,
        settings: {
          ...prev.settings,
          ...updates,
        },
      }));
    },
    [updateSchema]
  );

  const importSchema = useCallback((json: unknown): { success: boolean; errors?: string[] } => {
    const result = validatePortalSchema(json);

    if (result.success && result.data) {
      setSchema(result.data);
      setSelectedPageId(result.data.pages[0]?.id ?? null);
      return { success: true };
    }

    return { success: false, errors: result.errors };
  }, []);

  const exportSchema = useCallback(() => schema, [schema]);

  const resetSchema = useCallback(() => {
    const newSchema = createDefaultSchema();
    setSchema(newSchema);
    setSelectedPageId(newSchema.pages[0]?.id ?? null);
  }, []);

  return {
    schema,
    selectedPageId,
    selectedPage,
    setSelectedPageId,
    addSnippet,
    updateSnippet,
    removeSnippet,
    addPage,
    updatePage,
    removePage,
    reorderPages,
    addBlock,
    updateBlock,
    removeBlock,
    reorderBlocks,
    updateSchemaName,
    updateSettings,
    importSchema,
    exportSchema,
    resetSchema,
  };
}
